import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { statusService } from "/app/src/services";
import HeroSelect from "/app/src/components/HeroUi/Select";
import HeroButton from "/app/src/components/HeroUi/Button";
import { Search } from "/app/src/components/generic/components/search";
import { buildParams } from "/app/src/helpers";

/**
 * Search and filtering controls for the orders table
 * found in the Order Builder integration
 */
export default function InnerControls({
  integrationId,
  handleSearch,
  setStatusIds,
  updateStatusIds,
  deleteOrders,
  selectedOrderIds,
}: {
  integrationId?: number;
  handleSearch: (searchString: string) => void;
  setStatusIds: (statusIds: string[]) => void;
  updateStatusIds: (statusId: number) => void;
  deleteOrders: () => void;
  selectedOrderIds: number[];
}) {
  const { t } = useTranslation();
  const [selectedStatus, setSelectedStatus] = useState<string>(undefined);
  const { data: statuses, isLoading } = useQuery({
    queryKey: ["statuses"],
    queryFn: () =>
      statusService.getAll(
        buildParams({
          integrationId,
          includeDefaults: true,
        }),
      ),
    select: (data) => data.statuses,
    initialData: { statuses: [] },
    enabled: Boolean(integrationId),
  });
  const handleSelectStatus = useCallback(
    (e) => {
      setSelectedStatus(e.target.value);
    },
    [setSelectedStatus],
  );

  const handleStatusChange = useCallback(
    (value) => {
      //CSV should be converted to a list of strings
      const statuses = value.target.value.split(",");
      setStatusIds(statuses);
    },
    [setStatusIds],
  );

  const handleUpdateClick = useCallback(() => {
    updateStatusIds(parseInt(selectedStatus));
  }, [selectedStatus, updateStatusIds]);

  const handleDeleteClick = useCallback(() => {
    deleteOrders();
  }, [deleteOrders]);
  return (
    <div className="pb-[10px] flex justify-between items-center gap-[42px]">
      <div className="flex flex-grow gap-[42px]">
        <Search box={false} setSearchString={handleSearch} />
        <HeroSelect
          isDisabled={isLoading}
          selectionMode="multiple"
          placeholder={t("translation:filter_by_status")}
          onChange={handleStatusChange}
          items={statuses.map((status) => {
            return {
              key: status.id,
              value: status.id,
              label: status.name,
            };
          })}
          ariaLabel={t("translation:filter_by_status")}
        />
        <HeroSelect
          isDisabled={isLoading}
          placeholder={t("translation:update_selected_status")}
          onChange={handleSelectStatus}
          items={statuses
            .filter((status) => status.updatableStatus === true)
            .map((status) => {
              return {
                key: status.id,
                value: status.id,
                label: status.name,
              };
            })}
          ariaLabel={t("translation:update_selected_status")}
        />
      </div>
      <div className="flex gap-2">
        <HeroButton
          onClick={handleUpdateClick}
          isDisabled={
            selectedStatus === undefined || selectedOrderIds.length === 0
          }
          size="md"
          fullWidth
          color="primary"
        >
          {t("translation:update")}
        </HeroButton>
        <HeroButton
          onClick={handleDeleteClick}
          isDisabled={selectedOrderIds.length === 0}
          size="md"
          fullWidth
          color="red"
        >
          {t("translation:delete")}
        </HeroButton>
      </div>
    </div>
  );
}
